import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, TextField, Button, Alert, CircularProgress, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import { useTranslation } from 'react-i18next'
import { authService } from '@/services/authService'
import AuthShell from '@/components/AuthShell'
import type { ResetPasswordCredentials } from '@/types/auth'

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [formData, setFormData] = useState<ResetPasswordCredentials>({
    user_id: '',
    timestamp: 0,
    signature: '',
    password: '',
  })
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const user_id = searchParams.get('user_id')
    const timestamp = searchParams.get('timestamp')
    const signature = searchParams.get('signature')

    if (!user_id || !timestamp || !signature) {
      setError(t('invalidResetLink'))
      return
    }

    setFormData({
      user_id,
      timestamp: parseInt(timestamp, 10),
      signature,
      password: '',
    })
  }, [searchParams, t])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'password_confirm') {
      setPasswordConfirm(value)
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    if (formData.password !== passwordConfirm) {
      setError(t('passwordsDoNotMatch'))
      setIsLoading(false)
      return
    }

    if (formData.password.length < 8) {
      setError(t('passwordMinLength'))
      setIsLoading(false)
      return
    }

    try {
      await authService.resetPassword(formData)
      setSuccess(true)

      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err) {
      if (err instanceof Error && 'data' in err) {
        const apiError = err as Error & { data?: Record<string, unknown> }
        if (apiError.data) {
          const data = apiError.data
          if (Array.isArray(data.password)) {
            setError(data.password[0] as string)
          } else if (Array.isArray(data.non_field_errors)) {
            setError(data.non_field_errors[0] as string)
          } else if (typeof data.detail === 'string') {
            setError(data.detail)
          } else {
            setError(err.message)
          }
        } else {
          setError(err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : t('resetPasswordFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <AuthShell
        title={t('passwordResetSuccessTitle')}
        subtitle={t('passwordResetSuccessBody')}
        icon={<CheckCircleOutlineIcon />}
        iconTone="success"
      >
        <Typography variant="body2" align="center" color="text.secondary">
          {t('redirectingToLogin')}
        </Typography>
      </AuthShell>
    )
  }

  if (!formData.user_id || !formData.timestamp || !formData.signature) {
    return (
      <AuthShell
        title={t('invalidResetLink')}
        subtitle={error || t('invalidLinkExpired')}
        icon={<ErrorOutlineIcon />}
        iconTone="error"
      >
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => navigate('/send-reset-link')}
          sx={{ py: 1.25 }}
        >
          {t('requestNewResetLink')}
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={t('setNewPasswordTitle')} subtitle={t('setNewPasswordSubtitle')} icon={<VpnKeyOutlinedIcon />}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="password"
          label={t('newPassword')}
          name="password"
          type="password"
          autoComplete="new-password"
          autoFocus
          value={formData.password}
          onChange={handleInputChange}
          disabled={isLoading}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password_confirm"
          label={t('confirmNewPassword')}
          type="password"
          id="password_confirm"
          autoComplete="new-password"
          value={passwordConfirm}
          onChange={handleInputChange}
          disabled={isLoading}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 2.5, mb: 1, py: 1.25 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : t('resetPassword')}
        </Button>
      </Box>
    </AuthShell>
  )
}
